import { type ReactElement, type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from '../i18n/useLanguage.js';
import { Button } from './Button.js';

export interface DialogProps {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  /**
   * `medium` and `large` both cap the panel's height to the viewport and
   * scroll the body instead of the page; `large` is also noticeably wider,
   * for dense content like tables.
   */
  readonly size?: 'default' | 'medium' | 'large';
}

/**
 * A modal that behaves like one: Escape closes it, a click on the backdrop
 * closes it, focus lands inside it on open and the page behind stops scrolling.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  size = 'default',
}: DialogProps): ReactElement {
  const t = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`dialog${size === 'default' ? '' : ` dialog--${size}`}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="dialog__header">
          <h2>{title}</h2>
          <Button variant="ghost" small onClick={onClose} aria-label={t.common.close}>
            ✕
          </Button>
        </div>
        <div className="dialog__body">{children}</div>
        {footer !== undefined ? (
          <div className="row row--end" style={{ marginTop: 'var(--space-4)' }}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
